import { FC } from 'react';

import { BannerModerationStatus } from 'utils/constants/banner';
import { CampaignPauseReason, CampaignStatus } from 'utils/constants/dashboard';

import useTrls from 'hooks/translation/useTrls';

import { BannedForAllSourcesTooltip } from 'components/Banner/Actions/Message/BannedForAllSourcesTooltip/BannedForAllSourcesTooltip';
import CampaignStatusPaused from 'components/CampaignCard/StatusPaused';

import { FrontendStatusType } from 'store/models/dashboard';
import { useSelector } from 'store/utils';

interface MessageProps {
    campaignStatus: FrontendStatusType;
    campaignId: number;
    moderationStatus: BannerModerationStatus;
    enabled: boolean;
    bannedAsAdvertisement: boolean;
    bannerId: number;
    bannedForAllSources: boolean;
    moderationReason: string;
    bannersCount: number;
}

const TrlKeys = {
    banner: {
        new: {
            content: 'banner.actions.message.status.new',
            times: 'banner.actions.message.status.new.times',
        },
        active: 'banner.actions.message.status.active',
        banned: 'banner.actions.message.status.banned',
        deactivated: 'banner.actions.message.status.deactivated',
    },
    campaign: {
        stopped: 'campaign.actions.status.stopped.campaign',
        archived: 'campaign.actions.status.archived.campaign',
    },
};

const Message: FC<MessageProps> = ({
    campaignStatus,
    campaignId,
    moderationStatus,
    enabled,
    bannedAsAdvertisement,
    bannerId,
    bannedForAllSources,
    moderationReason,
    bannersCount,
}) => {
    const { balance: userBalance, realBalance } = useSelector((state) => state.balance);

    const trl = useTrls();

    const bannerStatuses: {
        [key in BannerModerationStatus]: JSX.Element;
    } = {
        [BannerModerationStatus.New]: (
            <div data-qa="ad-block__campaign-state">
                {trl(TrlKeys.banner.new.content)}
                <br />
                {trl(TrlKeys.banner.new.times)}
            </div>
        ),
        [BannerModerationStatus.Moderated]: <div data-qa="ad-block__campaign-state">{trl(TrlKeys.banner.active)}</div>,
        [BannerModerationStatus.Banned]:
            !bannedAsAdvertisement && !bannedForAllSources ? (
                <div data-qa="ad-block__campaign-state">{trl(TrlKeys.banner.banned)}</div>
            ) : (
                <BannedForAllSourcesTooltip
                    bannedForAllSources={bannedForAllSources}
                    bannedAsAdvertisement={bannedAsAdvertisement}
                    bannersCount={bannersCount}
                    campaignId={campaignId}
                    bannerId={bannerId}
                    moderationReason={moderationReason}
                />
            ),
    };

    const campaignStatuses: {
        [key in Exclude<
            CampaignStatus,
            CampaignStatus.Active | CampaignStatus.ActiveWithDelayedStart | CampaignStatus.InactiveDueToEndDate
        >]: JSX.Element;
    } = {
        [CampaignStatus.Blocked]: <div data-qa="ad-block__campaign-state">{trl(TrlKeys.campaign.stopped)}</div>,
        [CampaignStatus.Archived]: <div data-qa="ad-block__campaign-state">{trl(TrlKeys.campaign.archived)}</div>,
    };

    const isCampaignBlockedOrArchived =
        campaignStatus === CampaignStatus.Blocked || campaignStatus === CampaignStatus.Archived;
    const isCampaignPaused =
        campaignStatus === CampaignPauseReason.BudgetExceeded ||
        campaignStatus === CampaignPauseReason.DailyBudgetExceedStatus;

    if (campaignStatuses.hasOwnProperty(campaignStatus) && isCampaignBlockedOrArchived) {
        return campaignStatuses[campaignStatus];
    }
    if (isCampaignPaused) {
        return (
            <CampaignStatusPaused
                userBalance={userBalance}
                realBalance={realBalance}
                campaignId={campaignId}
                frontendStatus={campaignStatus}
                isBanner
            />
        );
    }
    if (bannerStatuses.hasOwnProperty(moderationStatus)) {
        return enabled ? (
            bannerStatuses[moderationStatus]
        ) : (
            <div data-qa="ad-block__campaign-state">{trl(TrlKeys.banner.deactivated)}</div>
        );
    }
    return null;
};

export default Message;
